import { Asset, AssetDependency, AssetState, DependencyStatus } from '@/api/generatedApi';
import { useGetAssetDependencies } from '@/hooks/api/assets/useGetAssetDependencies';
import { isNodeNameMatchForPath } from '@physna/core/domain/viewer/selection-sync-messaging';
import { useResolveFlow } from './useResolveFlow';
import { getFilename } from '@/utils/path';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  SxProps,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { ChevronRight, ErrorOutline, ExpandMore, LinkOff, SyncAlt, UploadFile, ViewInAr } from '@mui/icons-material';
import { StyledPopover, StyledList, StyledListItem, StyledListHeader } from '@/components/AssetsPage/PopoverList';
import { AssetSelectionModal } from '@/components/modals/AssetSelectionModal';
import { FileInputs } from '@/components/Upload/FileInputs/FileInputs';
import { UploadDrawer } from '@/components/Upload/UploadDrawer/UploadDrawer';
import { useDependencyUpload } from '@/hooks/useDependencyUpload';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { MissingDependencyIcon } from '@/components/MissingDependencyIcon/MissingDependencyIcon';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { ConfirmDialog } from './ConfirmDialog';

const useStyles = createUseStyles(() => ({
  DependencyTreeView_LoadState: {
    marginLeft: '15px',
    height: '32px',
  },
  DependencyTreeView_TreeView: {
    paddingLeft: '.5rem',
  },
  TreeItemWrapper_label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '2px 0',
    height: '1.5rem',
  },
  TreeItemWrapper_text: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    fontSize: '0.875rem',
  },
  TreeItemWrapper_resolveButton: {
    padding: '4px',
    marginLeft: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  TreeItemWrapper_filename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    maxWidth: 'calc(100% - 20px)',
  },
  TreeItemWrapper_missingIcon: {
    marginRight: '6px',
    display: 'flex',
  },
}));

interface LabelWrapperProperties {
  children: React.ReactElement;
  isMissing?: boolean;
  isMissingTooltip?: ReactNode;
}

const LabelWrapper = ({ children, isMissing, isMissingTooltip }: LabelWrapperProperties) => {
  return isMissing ? <Tooltip title={isMissingTooltip}>{children}</Tooltip> : children;
};

interface TreeItemWrapperProperties {
  id: string;
  path: string;
  displayName?: string;
  children?: ReactNode;
  sx?: SxProps;
  isMissing?: boolean;
  isResolved?: boolean;
  isHighlighted?: boolean;
  onClick?: () => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  assemblyId?: string;
  isUploading?: boolean;
  isIndexing?: boolean;
  hasMissingChildDependencies?: boolean;
}

export const TreeItemWrapper = ({
  id,
  path,
  displayName,
  sx,
  children,
  isMissing,
  isResolved,
  isHighlighted = false,
  onClick,
  onResolveClick,
  onSwitchClick,
  assemblyId,
  isUploading = false,
  isIndexing = false,
  hasMissingChildDependencies = false,
}: TreeItemWrapperProperties) => {
  const c = useStyles();
  const shouldShowResolveButton = isMissing && onResolveClick;
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | undefined>();

  const hasChildren = !!children && (!Array.isArray(children) || children.length > 0);

  const handleContextMenu = (event: React.MouseEvent) => {
    if (!isResolved || !onSwitchClick) return;
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  return (
    <>
      <TreeItem
        itemId={id}
        sx={{
          ...sx,
          ...(isHighlighted
            ? {
                '& > .MuiTreeItem-content': {
                  backgroundColor: 'action.selected',
                },
              }
            : {}),
        }}
        {...(!hasChildren && isMissing ? { slots: { icon: MissingDependencyIcon } } : {})}
        label={
          <LabelWrapper isMissing={isMissing} isMissingTooltip={getFilename(path)}>
            <Box
              className={c.TreeItemWrapper_label}
              onClick={event => {
                event.stopPropagation();
                onClick?.();
              }}
              onContextMenu={handleContextMenu}
              gap={1}
              style={{ cursor: onClick ? 'pointer' : undefined }}
            >
              <Box className={c.TreeItemWrapper_text}>
                {isMissing && hasChildren && (
                  <span className={c.TreeItemWrapper_missingIcon} data-testid='MissingDependenciesIcon'>
                    <MissingDependencyIcon hideTooltip />
                  </span>
                )}
                {isMissing ? (
                  <button
                    type='button'
                    className={c.TreeItemWrapper_filename}
                    onClick={event => {
                      event.stopPropagation();
                      void navigator.clipboard.writeText(getFilename(path));
                    }}
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    {getFilename(path)}
                  </button>
                ) : (
                  <Tooltip title={displayName ?? getFilename(path)}>
                    <span className={c.TreeItemWrapper_filename}>{displayName ?? getFilename(path)}</span>
                  </Tooltip>
                )}
              </Box>
              {isIndexing && (
                <LinearProgress
                  sx={{ width: '28px', ml: 1, mr: '2px', flexShrink: 0 }}
                  data-testid='dependency-indexing-progress'
                />
              )}
              {hasMissingChildDependencies && (
                <Tooltip title='Contains missing dependencies'>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '26px',
                      height: '26px',
                      flexShrink: 0,
                    }}
                  >
                    <ErrorOutline
                      data-testid='MissingAssetIcon'
                      sx={{ color: 'warning.dark', fontSize: '12px', cursor: 'default' }}
                    />
                  </Box>
                </Tooltip>
              )}
              {shouldShowResolveButton &&
                (isUploading ? (
                  <IconButton size='small' disabled>
                    <CircularProgress size={16} />
                  </IconButton>
                ) : (
                  <IconButton
                    data-testid='resolve-dependency-button'
                    className={c.TreeItemWrapper_resolveButton}
                    onClick={event => {
                      event.stopPropagation();
                      onResolveClick(event, path, assemblyId ?? '');
                    }}
                    size='small'
                    color='error'
                    sx={{ '& .MuiSvgIcon-root': { color: 'error.dark' } }}
                    aria-label={`Resolve missing dependency ${getFilename(path)}`}
                  >
                    <LinkOff sx={{ fontSize: '16px' }} />
                  </IconButton>
                ))}
            </Box>
          </LabelWrapper>
        }
      >
        {children}
      </TreeItem>
      {isResolved && onSwitchClick && (
        <Menu
          open={contextMenu !== undefined}
          onClose={() => setContextMenu(undefined)}
          anchorReference='anchorPosition'
          anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        >
          <MenuItem
            data-testid='switch-dependency-button'
            onClick={event => {
              setContextMenu(undefined);
              onSwitchClick(
                event as unknown as React.MouseEvent<HTMLButtonElement>,
                path,
                assemblyId ?? '',
                displayName
              );
            }}
          >
            <ListItemIcon>
              <SyncAlt sx={{ fontSize: '16px' }} />
            </ListItemIcon>
            <ListItemText>Switch resolved asset</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

const getUniqueItemId = (item: AssetDependency, parentPath: string = ''): string => {
  const baseId = item.asset?.id ? `${item.asset.id}-${item.path}` : `missing-${item.path}`;
  return parentPath ? `${parentPath}/${baseId}` : baseId;
};

interface DependencyListProperties {
  dependencies: AssetDependency[];
  parentItemId: string;
  level: number;
  assemblyId: string;
  assemblyPath: string;
  onTreeItemClick?: (itemId: string, context?: DependencyContext) => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  uploadingPaths: Set<string>;
  highlightedAssetIds?: string[];
  highlightedNodeNames?: string[];
}

const DependencyList = ({
  dependencies,
  parentItemId,
  level,
  assemblyId,
  assemblyPath,
  onTreeItemClick,
  onResolveClick,
  onSwitchClick,
  uploadingPaths,
  highlightedAssetIds,
  highlightedNodeNames,
}: DependencyListProperties) =>
  dependencies.map(dependency => {
    const itemId = parentItemId ? `${parentItemId}/${getUniqueItemId(dependency)}` : getUniqueItemId(dependency);
    const context: DependencyContext = {
      dependencyPath: dependency.path,
      assemblyId,
      assemblyName: getFilename(assemblyPath),
      assetPath: dependency.asset?.path,
    };
    return dependency.hasDependencies && dependency.asset ? (
      <DependencyTreeItem
        key={itemId}
        item={dependency}
        parentPath={parentItemId}
        level={level}
        assemblyId={assemblyId}
        assemblyPath={assemblyPath}
        onTreeItemClick={onTreeItemClick}
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        uploadingPaths={uploadingPaths}
        highlightedAssetIds={highlightedAssetIds}
        highlightedNodeNames={highlightedNodeNames}
      />
    ) : (
      <TreeItemWrapper
        key={itemId}
        id={itemId}
        path={dependency.path}
        displayName={dependency.asset ? getFilename(dependency.asset.path) : undefined}
        assemblyId={assemblyId}
        onClick={() => onTreeItemClick?.(dependency.asset?.id ?? '', context)}
        isMissing={!dependency.asset}
        isResolved={dependency.status === DependencyStatus.Resolved}
        isHighlighted={
          (!!dependency.asset?.id && !!highlightedAssetIds?.includes(dependency.asset.id)) ||
          isNodeNameMatchForPath(highlightedNodeNames, dependency.path)
        }
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        isUploading={uploadingPaths.has(dependency.path)}
        isIndexing={dependency.asset?.state === AssetState.Indexing}
        hasMissingChildDependencies={dependency.asset?.isMissingDependencies ?? false}
      />
    );
  });

interface DependencyTreeItemProperties {
  item: AssetDependency;
  parentPath?: string;
  level?: number;
  assemblyId: string;
  assemblyPath: string;
  onTreeItemClick?: (itemId: string, context?: DependencyContext) => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  uploadingPaths?: Set<string>;
  highlightedAssetIds?: string[];
  highlightedNodeNames?: string[];
}

const DependencyTreeItem = ({
  item,
  parentPath,
  level = 0,
  assemblyId,
  assemblyPath,
  onTreeItemClick,
  onResolveClick,
  onSwitchClick,
  uploadingPaths = new Set(),
  highlightedAssetIds,
  highlightedNodeNames,
}: DependencyTreeItemProperties) => {
  const c = useStyles();
  const itemReference = useRef<HTMLDivElement>(null);
  const { dependencies, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetAssetDependencies({
    assetId: item.asset?.id,
    enabled: item.hasDependencies,
  });

  useInfiniteScroll(itemReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const currentItemId = getUniqueItemId(item, parentPath);

  if (error) {
    return;
  }

  if (isLoading) {
    return (
      <Skeleton
        data-testid='dependency-tree-item-skeleton'
        width={180}
        className={c.DependencyTreeView_LoadState}
        variant='text'
      />
    );
  }

  const baseLevel = level + 1;
  const hasMissingChildDependencies =
    (item.asset?.isMissingDependencies ?? false) ||
    (dependencies?.some(dep => !dep.asset || dep.asset?.isMissingDependencies) ?? false);

  return (
    <div ref={itemReference}>
      <TreeItemWrapper
        id={currentItemId}
        path={item.path}
        displayName={item.asset ? getFilename(item.asset.path) : undefined}
        assemblyId={assemblyId}
        onClick={() => {
          const context: DependencyContext = {
            dependencyPath: item.path,
            assemblyId,
            assemblyName: getFilename(assemblyPath),
            assetPath: item.asset?.path,
          };
          onTreeItemClick?.(item.asset?.id ?? '', context);
        }}
        isMissing={!item.asset}
        isResolved={item.status === DependencyStatus.Resolved}
        isHighlighted={
          (!!item.asset?.id && !!highlightedAssetIds?.includes(item.asset.id)) ||
          isNodeNameMatchForPath(highlightedNodeNames, item.path)
        }
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        isUploading={uploadingPaths.has(item.path)}
        isIndexing={item.asset?.state === AssetState.Indexing}
        hasMissingChildDependencies={hasMissingChildDependencies}
      >
        {dependencies && (
          <DependencyList
            dependencies={dependencies}
            parentItemId={currentItemId}
            level={baseLevel}
            assemblyId={item.asset?.id ?? ''}
            assemblyPath={item.asset?.path ?? item.path}
            onTreeItemClick={onTreeItemClick}
            onResolveClick={onResolveClick}
            onSwitchClick={onSwitchClick}
            uploadingPaths={uploadingPaths}
            highlightedAssetIds={highlightedAssetIds}
            highlightedNodeNames={highlightedNodeNames}
          />
        )}
        {isFetchingNextPage && (
          <Box className={c.DependencyTreeView_LoadState}>
            <Skeleton width={180} variant='text' />
          </Box>
        )}
      </TreeItemWrapper>
    </div>
  );
};

export interface DependencyContext {
  dependencyPath: string;
  assemblyId: string;
  assemblyName: string;
  assetPath?: string;
}

export interface DependencyTreeViewProperties {
  asset: Asset;
  onTreeItemClick?: (itemId: string, context?: DependencyContext) => void;
  highlightedAssetIds?: string[];
  highlightedNodeNames?: string[];
}

export const DependencyTreeView = ({
  asset,
  onTreeItemClick,
  highlightedAssetIds,
  highlightedNodeNames,
}: DependencyTreeViewProperties) => {
  const treeViewReference = useRef<HTMLDivElement>(null);
  const [hasIndexingDeps, setHasIndexingDeps] = useState(false);
  const { dependencies, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetAssetDependencies({
    assetId: asset.id,
    refetchInterval: hasIndexingDeps ? 15_000 : false,
  });

  useInfiniteScroll(treeViewReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    setHasIndexingDeps(dependencies?.some(dep => dep.asset?.state === AssetState.Indexing) ?? false);
  }, [dependencies]);

  const {
    handleResolveClick,
    handleSwitchClick,
    handleExistingAssetClick,
    handleUploadAssetClick,
    handleAssetSelect,
    resolveByUpload,
    isResolvePopoverOpen,
    resolveAnchorElement,
    closeResolvePopover,
    isAssetSelectionOpen,
    setIsAssetSelectionOpen,
    isSwitchMode,
    pendingResolvePath,
    pendingAssemblyId,
    getConfirmDialogProps,
  } = useResolveFlow();

  const [resolveTargetAssemblyId, setResolveTargetAssemblyId] = useState<string | undefined>();

  const previousIsAssetSelectionOpen = useRef(false);
  useEffect(() => {
    if (isAssetSelectionOpen && !previousIsAssetSelectionOpen.current) {
      setResolveTargetAssemblyId(pendingAssemblyId.current || undefined);
    }
    if (!isAssetSelectionOpen) {
      setResolveTargetAssemblyId(undefined);
    }
    previousIsAssetSelectionOpen.current = isAssetSelectionOpen;
  }, [isAssetSelectionOpen, pendingAssemblyId]);

  const { excludedResolveTargetIds } = useGetAssetDependencies({
    assetId: resolveTargetAssemblyId,
    enabled: !!resolveTargetAssemblyId,
    includeExcludedResolveTargets: true,
  });

  const {
    fileInputReference,
    folderInputReference,
    files,
    uploadProgress,
    drawerOpen,
    handleDrawerClose,
    uploadingPaths,
    handleUploadClick,
    fileInputProps,
    pendingFile,
    pendingDependencyPath,
    confirmOpen,
    confirmUpload,
    cancelUpload,
  } = useDependencyUpload({
    onUploaded: resolveByUpload,
  });

  const c = useStyles();

  if (!dependencies || error) {
    return;
  }

  return (
    <>
      <div ref={treeViewReference}>
        <SimpleTreeView
          key={asset.id}
          aria-label='dependency tree'
          data-testid='dependency-tree'
          className={c.DependencyTreeView_TreeView}
          slots={{
            expandIcon: ChevronRight,
            collapseIcon: ExpandMore,
          }}
          sx={{
            '& .MuiTreeItem-iconContainer': {
              color: 'text.primary',
              width: 'auto',
              minWidth: 'auto',
              '& svg': {
                fontSize: '1.25rem',
              },
              '& svg.missing-dep-icon': {
                fontSize: '14px',
              },
            },
            '& .MuiTreeItem-groupTransition': {
              ml: 1,
              pl: 1,
            },
          }}
        >
          <DependencyList
            dependencies={dependencies}
            parentItemId=''
            level={0}
            assemblyId={asset.id}
            assemblyPath={asset.path}
            onTreeItemClick={onTreeItemClick}
            onResolveClick={handleResolveClick}
            onSwitchClick={handleSwitchClick}
            uploadingPaths={uploadingPaths}
            highlightedAssetIds={highlightedAssetIds}
            highlightedNodeNames={highlightedNodeNames}
          />
          {isFetchingNextPage && (
            <Box className={c.DependencyTreeView_LoadState}>
              <Skeleton width={180} variant='text' />
            </Box>
          )}
        </SimpleTreeView>
      </div>
      <StyledPopover
        open={isResolvePopoverOpen}
        anchorEl={resolveAnchorElement}
        onClose={closeResolvePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <ListItemText>Resolve Dependency</ListItemText>
          </StyledListHeader>
          <Divider />
          <StyledListItem onClick={handleExistingAssetClick} data-testid='resolve-existing-asset'>
            <ListItemIcon>
              <ViewInAr />
            </ListItemIcon>
            <ListItemText>Existing Asset</ListItemText>
          </StyledListItem>
          <Divider />
          <StyledListItem onClick={() => handleUploadAssetClick(handleUploadClick)} data-testid='resolve-upload-asset'>
            <ListItemIcon>
              <UploadFile />
            </ListItemIcon>
            <ListItemText>Upload Asset</ListItemText>
          </StyledListItem>
        </StyledList>
      </StyledPopover>
      <AssetSelectionModal
        open={isAssetSelectionOpen}
        onClose={() => setIsAssetSelectionOpen(false)}
        onAssetSelect={handleAssetSelect}
        title={isSwitchMode ? 'Switch Resolved Asset' : 'Resolve Dependency'}
        description={
          isSwitchMode
            ? `Select a different asset to resolve "${getFilename(pendingResolvePath)}".`
            : `Select an existing asset to resolve the missing dependency "${getFilename(pendingResolvePath)}".`
        }
        initialFolderId={asset.folderId}
        excludeAssetIds={excludedResolveTargetIds}
        excludeTooltip='This asset cannot be selected because it would create a circular dependency'
      />
      <ConfirmDialog
        {...(() => {
          const resolveProperties = getConfirmDialogProps(pendingDependencyPath, pendingFile);
          return {
            ...resolveProperties,
            open: resolveProperties.open || confirmOpen,
            onConfirm: resolveProperties.open ? resolveProperties.onConfirm : confirmUpload,
            onCancel: resolveProperties.open ? resolveProperties.onCancel : cancelUpload,
          };
        })()}
      />
      <FileInputs
        fileInputProps={fileInputProps}
        fileInputReference={fileInputReference}
        folderInputReference={folderInputReference}
      />
      <UploadDrawer files={files} uploadProgress={uploadProgress} isOpen={drawerOpen} onClose={handleDrawerClose} />
    </>
  );
};
